import { create } from 'zustand';
import { listsApi } from '../lib/api';
import type { List, ListItemView, ListEntityKind, FilterSpec } from '../types/list';
import type { PaginationMeta } from '../types';

type Scope = 'mine' | 'shared' | 'team';

interface ListState {
  scope: Scope;
  lists: List[];
  isLoadingList: boolean;
  listError: string | null;
  currentList: List | null;
  items: ListItemView[];
  itemsPage: number;
  itemsPagination: PaginationMeta | null;
  itemsSearch: string;
  isLoadingMore: boolean;
  isLoadingAll: boolean;
  isLoadingDetail: boolean;
  detailError: string | null;
  isSaving: boolean;
  saveError: string | null;
  setScope: (s: Scope) => void;
  fetchLists: () => Promise<void>;
  fetchList: (id: string) => Promise<void>;
  fetchItems: (id: string) => Promise<void>;
  setItemsSearch: (id: string, term: string) => Promise<void>;
  fetchMoreItems: (id: string) => Promise<void>;
  loadAllItems: (id: string) => Promise<void>;
  createList: (input: { name: string; description?: string | null }) => Promise<List>;
  renameList: (id: string, name: string) => Promise<void>;
  deleteList: (id: string) => Promise<void>;
  addItems: (id: string, items: { kind: ListEntityKind; id: string }[]) => Promise<number>;
  removeItem: (id: string, itemId: string) => Promise<void>;
  reorder: (id: string, orderedItemIds: string[]) => Promise<void>;
  createSmartList: (input: { name: string; smartEntityKind: ListEntityKind; filterSpec: FilterSpec }) => Promise<List>;
  updateFilter: (id: string, filterSpec: FilterSpec) => Promise<void>;
  addFromFilter: (id: string, kind: ListEntityKind, filterSpec: FilterSpec) => Promise<number>;
  setSort: (id: string, field: string | null, dir: 'asc' | 'desc' | null) => Promise<void>;
  applySort: (id: string, field: string, dir: 'asc' | 'desc') => Promise<void>;
}

export const useListStore = create<ListState>((set, get) => ({
  scope: 'mine',
  lists: [],
  isLoadingList: false,
  listError: null,
  currentList: null,
  items: [],
  itemsPage: 1,
  itemsPagination: null,
  itemsSearch: '',
  isLoadingMore: false,
  isLoadingAll: false,
  isLoadingDetail: false,
  detailError: null,
  isSaving: false,
  saveError: null,
  setScope: (scope) => { set({ scope }); get().fetchLists(); },
  fetchLists: async () => {
    set({ isLoadingList: true, listError: null });
    try {
      const { lists } = await listsApi.list(get().scope);
      set({ lists, isLoadingList: false });
    } catch (e) {
      set({ listError: e instanceof Error ? e.message : 'Failed to load lists', isLoadingList: false });
    }
  },
  fetchList: async (id) => {
    // Navigating to a different list must not carry the previous list's search.
    if (get().currentList?.id !== id) set({ itemsSearch: '' });
    set({ isLoadingDetail: true, detailError: null });
    try {
      const { list } = await listsApi.get(id);
      set({ currentList: list, isLoadingDetail: false });
    } catch (e) {
      set({ detailError: e instanceof Error ? e.message : 'Failed to load list', isLoadingDetail: false });
    }
  },
  fetchItems: async (id) => {
    const search = get().itemsSearch || undefined;
    const { items, pagination } = await listsApi.items(id, { page: 1, limit: 100, search });
    set({ items, itemsPagination: pagination, itemsPage: 1, isLoadingMore: false, isLoadingAll: false });
  },
  setItemsSearch: async (id, term) => {
    set({ itemsSearch: term });
    await get().fetchItems(id);
  },
  fetchMoreItems: async (id) => {
    const { itemsPagination, isLoadingMore, items, itemsPage } = get();
    if (isLoadingMore || !itemsPagination?.hasNextPage) return;
    set({ isLoadingMore: true });
    try {
      const next = itemsPage + 1;
      const { items: more, pagination } = await listsApi.items(id, { page: next, limit: 100, search: get().itemsSearch || undefined });
      set({ items: [...items, ...more], itemsPagination: pagination, itemsPage: next, isLoadingMore: false });
    } catch (e) {
      set({ isLoadingMore: false });
      throw e;
    }
  },
  loadAllItems: async (id) => {
    if (get().isLoadingAll) return;
    set({ isLoadingAll: true });
    try {
      const acc: ListItemView[] = [];
      let page = 1;
      let pagination: PaginationMeta;
      do {
        const res = await listsApi.items(id, { page, limit: 1000, search: get().itemsSearch || undefined });
        acc.push(...res.items);
        pagination = res.pagination;
        page += 1;
      } while (pagination.hasNextPage);
      set({ items: acc, itemsPagination: { ...pagination, hasNextPage: false }, itemsPage: page - 1, isLoadingAll: false });
    } catch (e) {
      set({ isLoadingAll: false });
      throw e;
    }
  },
  createList: async (input) => {
    set({ isSaving: true, saveError: null });
    try {
      const { list } = await listsApi.create(input);
      set({ isSaving: false });
      await get().fetchLists();
      return list;
    } catch (e) {
      set({ saveError: e instanceof Error ? e.message : 'Failed to create list', isSaving: false });
      throw e;
    }
  },
  renameList: async (id, name) => {
    await listsApi.update(id, { name });
    await get().fetchLists();
    if (get().currentList?.id === id) await get().fetchList(id);
  },
  deleteList: async (id) => {
    await listsApi.remove(id);
    await get().fetchLists();
  },
  addItems: async (id, items) => {
    const { added } = await listsApi.addItems(id, items);
    await get().fetchItems(id);
    await get().fetchList(id);
    return added;
  },
  removeItem: async (id, itemId) => {
    await listsApi.removeItem(id, itemId);
    set({ items: get().items.filter((i) => i.id !== itemId) });
    await get().fetchList(id);
  },
  reorder: async (id, orderedItemIds) => {
    await listsApi.reorder(id, orderedItemIds);
    await get().fetchItems(id);
  },
  createSmartList: async (input) => {
    set({ isSaving: true, saveError: null });
    try {
      const { list } = await listsApi.create({ name: input.name, kind: 'SMART', smartEntityKind: input.smartEntityKind, filterSpec: input.filterSpec });
      set({ isSaving: false });
      await get().fetchLists();
      return list;
    } catch (e) {
      set({ saveError: e instanceof Error ? e.message : 'Failed to create smart list', isSaving: false });
      throw e;
    }
  },
  updateFilter: async (id, filterSpec) => {
    await listsApi.update(id, { filterSpec });
    if (get().currentList?.id === id) await get().fetchList(id);
  },
  addFromFilter: async (id, kind, filterSpec) => {
    const { added } = await listsApi.addFromFilter(id, kind, filterSpec);
    await get().fetchItems(id);
    await get().fetchList(id);
    return added;
  },
  setSort: async (id, field, dir) => {
    await listsApi.update(id, { sortField: field, sortDir: dir });
    await get().fetchList(id);
    await get().fetchItems(id);
  },
  applySort: async (id, field, dir) => {
    await listsApi.sortItems(id, field, dir);
    await get().fetchItems(id);
  },
}));
